import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "./RestaurantContext";

/**
 * Explains a restricted location instead of letting someone find out by
 * clicking something that fails.
 *
 * This became necessary the day the free trial moved to one-per-account: before
 * that nobody landed in a restricted state on day one, and now every location
 * after an account's first does. The API has always enforced this correctly,
 * this is the part that tells the truth to the person looking at the screen.
 *
 * Deliberately not dismissible. It is not a notification, it is the current
 * state of the location, and it stops being shown when the state changes.
 */
const ReadOnlyBanner: React.FC = () => {
  const { t } = useTranslation();
  const { canWrite, currentState, restaurants, currentRestaurantId } = useRestaurant();

  if (canWrite || !currentState) return null;

  const isAdmin = (JSON.parse(localStorage.getItem("user") || "{}") as any).role === "admin";
  // With several locations, saying *which* one is restricted is the whole
  // message, since the switcher means the reader may be looking at a different one.
  const name = restaurants.length > 1
    ? restaurants.find((r) => r.id === currentRestaurantId)?.name
    : undefined;

  const reason = t(`readOnly.reason.${currentState}`, {
    defaultValue: t("readOnly.reason.none"),
  });

  return (
    <div
      role="status"
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-semibold">
            {name ? t("readOnly.titleNamed", { name }) : t("readOnly.title")}
          </span>{" "}
          <span>{reason}</span>{" "}
          <span className="text-amber-800">{t("readOnly.whatStillWorks")}</span>
        </div>
        {/* Only the owner can pay, so only the owner is sent to Billing. A
            manager needs to know why the button is dead, not to be pointed at a
            page they cannot act on. */}
        {isAdmin && (
          <Link
            to="/billing"
            className="shrink-0 px-3 py-1.5 bg-amber-600 text-white rounded font-semibold hover:bg-amber-700 transition"
          >
            {t("readOnly.cta")}
          </Link>
        )}
      </div>
    </div>
  );
};

export default ReadOnlyBanner;
